import { useSelector } from 'react-redux';

import { useGetCurrentUser } from '@highlight-cp/hooks';

import { useConfigCatFeatureFlag } from '../config-cat';
import { useFeatureFlagImpersonatorOverride } from '../hooks';
import type { FeatureFlagValue, SettingTypeOf } from '../types';
import { selectConfigCatInfo } from './store';

export function useFeatureFlag<T extends FeatureFlagValue = false>(
  key: string,
  defaultValue: T = false as T,
): { value: SettingTypeOf<T>; loading: boolean } {
  // TODO: Remove dependency on app-slice reducer
  // currently, the client app is not using the app-slice reducer and we should
  // eventually deprecate it from the highlighter-app and csm apps as well
  const { emailAddress, organizationId, roleId } = useSelector(selectConfigCatInfo);
  const { data: currentUser } = useGetCurrentUser();
  const { shouldUseImpersonator, impersonatorEmail } = useFeatureFlagImpersonatorOverride();

  // When override is active, use impersonator's email for feature flag evaluation
  // but keep target user's org/role (they're testing the feature in that context)
  const effectiveEmail = shouldUseImpersonator
    ? impersonatorEmail
    : (currentUser?.emailAddress ?? emailAddress);

  return useConfigCatFeatureFlag(key, defaultValue, {
    emailAddress: effectiveEmail,
    roleId: roleId ?? undefined,
    organizationId: currentUser?.organization?.id ?? organizationId,
  });
}
